import { useState } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { Slider } from "../components/ui/slider";
import { Switch } from "../components/ui/switch";
import {
  Sparkles,
  Wand2,
  FileText,
  RefreshCw,
  Save,
  CheckCircle2,
  XCircle,
  Loader2,
  AlertCircle,
  BookOpen,
  Edit,
  Eye,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { toast } from "sonner";

const AIContentStudio = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };

  // Generation state
  const [generating, setGenerating] = useState(false);
  const [regenerating, setRegenerating] = useState(null);
  const [saving, setSaving] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [expandedQuestion, setExpandedQuestion] = useState(null);

  // Topic generation form
  const [topicForm, setTopicForm] = useState({
    mode: "school",
    difficulty: 2,
    topic: "",
    passage_type: "static",
    length: "medium",
    num_questions: 5,
    category: "general",
  });

  // Text generation form
  const [textForm, setTextForm] = useState({
    source_text: "",
    mode: "school",
    difficulty: 2,
    num_questions: 5,
    category: "general",
  });

  const modes = [
    { id: "children", name: "Children (6-10)", desc: "Very simple, story-based" },
    { id: "school", name: "School (11-16)", desc: "Simple to moderate" },
    { id: "ssc", name: "SSC", desc: "Moderate to advanced" },
    { id: "banking", name: "Banking", desc: "Professional, finance-focused" },
    { id: "upsc", name: "UPSC Editorial", desc: "Sophisticated, analytical" },
  ];

  const categories = [
    { id: "general", name: "General" },
    { id: "economy", name: "Economy & Business" },
    { id: "polity", name: "Polity & Governance" },
    { id: "science", name: "Science & Technology" },
    { id: "history", name: "History & Culture" },
    { id: "environment", name: "Environment" },
    { id: "social", name: "Social Issues" },
    { id: "editorial", name: "Editorials" },
    { id: "story", name: "Stories" },
    { id: "biography", name: "Biographies" },
  ];

  const questionTypes = {
    direct: "Direct",
    inference: "Inference",
    vocabulary: "Vocabulary",
    tone: "Tone/Attitude",
    argument: "Argument",
    summary: "Summary/Main Idea",
  };

  const difficultyLabels = ["Very Easy", "Easy", "Medium", "Hard", "Very Hard"];

  const handleGenerateFromTopic = async () => {
    if (!topicForm.topic.trim()) {
      toast.error("Please enter a topic");
      return;
    }

    setGenerating(true);
    setGeneratedContent(null);

    try {
      const response = await axios.post(`${API}/ai/generate-from-topic`, topicForm, {
        headers,
        withCredentials: true,
        timeout: 60000, // 60 second timeout for AI generation
      });
      setGeneratedContent(response.data);
      toast.success("Content generated successfully!");
    } catch (error) {
      console.error("Generation error:", error);
      toast.error(error.response?.data?.detail || "Failed to generate content");
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateFromText = async () => {
    if (!textForm.source_text.trim() || textForm.source_text.length < 100) {
      toast.error("Please paste at least 100 characters of text");
      return;
    }

    setGenerating(true);
    setGeneratedContent(null);

    try {
      const response = await axios.post(`${API}/ai/generate-from-text`, textForm, {
        headers,
        withCredentials: true,
        timeout: 60000, // 60 second timeout for AI generation
      });
      setGeneratedContent(response.data);
      toast.success("Content generated successfully!");
    } catch (error) {
      console.error("Generation error:", error);
      toast.error(error.response?.data?.detail || "Failed to generate content");
    } finally {
      setGenerating(false);
    }
  };

  const handleRegenerate = async (type) => {
    if (!generatedContent) return;

    setRegenerating(type);

    try {
      const response = await axios.post(
        `${API}/ai/regenerate`,
        {
          passage_id: "temp",
          regenerate_type: type,
          existing_content: generatedContent,
        },
        { headers, withCredentials: true, timeout: 45000 } // 45 second timeout for regeneration
      );

      if (type === "passage") {
        setGeneratedContent((prev) => ({
          ...prev,
          title: response.data.title || prev.title,
          content: response.data.content || prev.content,
          summary: response.data.summary || prev.summary,
        }));
      } else {
        setGeneratedContent((prev) => ({
          ...prev,
          questions: response.data.questions || prev.questions,
        }));
      }
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} regenerated!`);
    } catch (error) {
      toast.error(`Failed to regenerate ${type}`);
    } finally {
      setRegenerating(null);
    }
  };

  const handleSaveDraft = async () => {
    if (!generatedContent) return;

    setSaving(true);

    try {
      const response = await axios.post(`${API}/ai/save-draft`, generatedContent, {
        headers,
        withCredentials: true,
      });
      toast.success(`Draft saved! Passage ID: ${response.data.passage_id}`);
      setGeneratedContent(null);
    } catch (error) {
      toast.error("Failed to save draft");
    } finally {
      setSaving(false);
    }
  };

  const handleEditContent = (field, value) => {
    setGeneratedContent((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEditQuestion = (index, field, value) => {
    setGeneratedContent((prev) => {
      const questions = [...prev.questions];
      questions[index] = { ...questions[index], [field]: value };
      return { ...prev, questions };
    });
  };

  const handleEditOption = (qIndex, oIndex, value) => {
    setGeneratedContent((prev) => {
      const questions = [...prev.questions];
      const options = [...questions[qIndex].options];
      options[oIndex] = value;
      questions[qIndex] = { ...questions[qIndex], options };
      return { ...prev, questions };
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-amber-500" />
            AI Content Studio
          </h2>
          <p className="text-slate-600 mt-1">
            Generate complete RC passages and questions using AI
          </p>
        </div>
        {generatedContent && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setEditMode(!editMode)}
              data-testid="toggle-edit-btn"
            >
              {editMode ? <Eye className="w-4 h-4 mr-2" /> : <Edit className="w-4 h-4 mr-2" />}
              {editMode ? "Preview" : "Edit"}
            </Button>
            <Button
              onClick={handleSaveDraft}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="save-draft-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save as Draft
            </Button>
          </div>
        )}
      </div>

      {/* Generation Forms */}
      {!generatedContent && (
        <Tabs defaultValue="topic" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="topic" data-testid="topic-tab">
              <Wand2 className="w-4 h-4 mr-2" />
              Generate from Topic
            </TabsTrigger>
            <TabsTrigger value="text" data-testid="text-tab">
              <FileText className="w-4 h-4 mr-2" />
              Generate from Text
            </TabsTrigger>
          </TabsList>

          {/* Generate from Topic */}
          <TabsContent value="topic">
            <Card>
              <CardHeader>
                <CardTitle>Generate from Topic</CardTitle>
                <CardDescription>
                  Enter a topic and AI will create a complete RC passage with questions
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Topic Input */}
                  <div className="md:col-span-2">
                    <Label>Topic *</Label>
                    <Input
                      placeholder="e.g., Climate change impact on agriculture, Digital India initiative, Ancient Indian trade routes..."
                      value={topicForm.topic}
                      onChange={(e) => setTopicForm((prev) => ({ ...prev, topic: e.target.value }))}
                      className="mt-1"
                      data-testid="topic-input"
                    />
                  </div>

                  {/* Mode Selection */}
                  <div>
                    <Label>Target Mode</Label>
                    <Select
                      value={topicForm.mode}
                      onValueChange={(v) => setTopicForm((prev) => ({ ...prev, mode: v }))}
                    >
                      <SelectTrigger className="mt-1" data-testid="mode-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {modes.map((m) => (
                          <SelectItem key={m.id} value={m.id}>
                            <div>
                              <span className="font-medium">{m.name}</span>
                              <span className="text-slate-500 ml-2 text-xs">{m.desc}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Category */}
                  <div>
                    <Label>Category</Label>
                    <Select
                      value={topicForm.category}
                      onValueChange={(v) => setTopicForm((prev) => ({ ...prev, category: v }))}
                    >
                      <SelectTrigger className="mt-1" data-testid="category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Difficulty Slider */}
                  <div className="md:col-span-2">
                    <Label>
                      Difficulty Level: <span className="font-bold">{difficultyLabels[topicForm.difficulty]}</span>
                    </Label>
                    <Slider
                      value={[topicForm.difficulty]}
                      onValueChange={([v]) => setTopicForm((prev) => ({ ...prev, difficulty: v }))}
                      min={0}
                      max={4}
                      step={1}
                      className="mt-3"
                      data-testid="difficulty-slider"
                    />
                    <div className="flex justify-between text-xs text-slate-500 mt-1">
                      {difficultyLabels.map((label, i) => (
                        <span key={i}>{label}</span>
                      ))}
                    </div>
                  </div>

                  {/* Passage Type */}
                  <div>
                    <Label>Passage Type</Label>
                    <Select
                      value={topicForm.passage_type}
                      onValueChange={(v) => setTopicForm((prev) => ({ ...prev, passage_type: v }))}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="static">Static (Evergreen)</SelectItem>
                        <SelectItem value="news">News-based</SelectItem>
                        <SelectItem value="editorial">Editorial</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Length */}
                  <div>
                    <Label>Passage Length</Label>
                    <Select
                      value={topicForm.length}
                      onValueChange={(v) => setTopicForm((prev) => ({ ...prev, length: v }))}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="short">Short (150-200 words)</SelectItem>
                        <SelectItem value="medium">Medium (250-350 words)</SelectItem>
                        <SelectItem value="long">Long (400-500 words)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Number of Questions */}
                  <div>
                    <Label>Number of Questions: {topicForm.num_questions}</Label>
                    <Slider
                      value={[topicForm.num_questions]}
                      onValueChange={([v]) => setTopicForm((prev) => ({ ...prev, num_questions: v }))}
                      min={3}
                      max={8}
                      step={1}
                      className="mt-3"
                      data-testid="questions-slider"
                    />
                  </div>
                </div>

                <Button
                  onClick={handleGenerateFromTopic}
                  disabled={generating || !topicForm.topic.trim()}
                  className="w-full h-12 text-base"
                  data-testid="generate-topic-btn"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Generating Content...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      Generate RC Set
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Generate from Text */}
          <TabsContent value="text">
            <Card>
              <CardHeader>
                <CardTitle>Generate from Text</CardTitle>
                <CardDescription>
                  Paste news, editorial, or any text and AI will convert it into an RC passage with questions
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label>Source Text *</Label>
                  <Textarea
                    placeholder="Paste your news article, editorial, or any text here (minimum 100 characters)..."
                    value={textForm.source_text}
                    onChange={(e) => setTextForm((prev) => ({ ...prev, source_text: e.target.value }))}
                    className="mt-1 min-h-[200px] font-serif"
                    data-testid="source-text-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    {textForm.source_text.length} characters
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label>Target Mode</Label>
                    <Select
                      value={textForm.mode}
                      onValueChange={(v) => setTextForm((prev) => ({ ...prev, mode: v }))}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {modes.map((m) => (
                          <SelectItem key={m.id} value={m.id}>
                            {m.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Category</Label>
                    <Select
                      value={textForm.category}
                      onValueChange={(v) => setTextForm((prev) => ({ ...prev, category: v }))}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="md:col-span-2">
                    <Label>
                      Difficulty Level: <span className="font-bold">{difficultyLabels[textForm.difficulty]}</span>
                    </Label>
                    <Slider
                      value={[textForm.difficulty]}
                      onValueChange={([v]) => setTextForm((prev) => ({ ...prev, difficulty: v }))}
                      min={0}
                      max={4}
                      step={1}
                      className="mt-3"
                    />
                  </div>

                  <div>
                    <Label>Number of Questions: {textForm.num_questions}</Label>
                    <Slider
                      value={[textForm.num_questions]}
                      onValueChange={([v]) => setTextForm((prev) => ({ ...prev, num_questions: v }))}
                      min={3}
                      max={8}
                      step={1}
                      className="mt-3"
                    />
                  </div>
                </div>

                <Button
                  onClick={handleGenerateFromText}
                  disabled={generating || textForm.source_text.length < 100}
                  className="w-full h-12 text-base"
                  data-testid="generate-text-btn"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Generating Content...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      Generate RC Set from Text
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* Generated Content Preview/Edit */}
      {generatedContent && (
        <div className="space-y-6">
          {/* Regenerate Controls */}
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm font-medium text-amber-800">Regenerate:</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleRegenerate("passage")}
                  disabled={!!regenerating}
                  className="bg-white"
                  data-testid="regen-passage-btn"
                >
                  {regenerating === "passage" ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-1" />
                  )}
                  Passage
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleRegenerate("questions")}
                  disabled={!!regenerating}
                  className="bg-white"
                  data-testid="regen-questions-btn"
                >
                  {regenerating === "questions" ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-1" />
                  )}
                  Questions
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleRegenerate("explanations")}
                  disabled={!!regenerating}
                  className="bg-white"
                  data-testid="regen-explanations-btn"
                >
                  {regenerating === "explanations" ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-1" />
                  )}
                  Explanations
                </Button>
                <div className="ml-auto flex items-center gap-2">
                  <Badge variant="outline" className="bg-white">
                    {generatedContent.mode?.toUpperCase()}
                  </Badge>
                  <Badge variant="outline" className="bg-white">
                    Level {generatedContent.difficulty}
                  </Badge>
                  <Badge className="bg-amber-600">DRAFT</Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Passage Content */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="w-5 h-5" />
                {editMode ? (
                  <Input
                    value={generatedContent.title}
                    onChange={(e) => handleEditContent("title", e.target.value)}
                    className="text-xl font-bold"
                    data-testid="edit-title"
                  />
                ) : (
                  generatedContent.title
                )}
              </CardTitle>
              <div className="flex gap-2 mt-2">
                <Badge>{generatedContent.category}</Badge>
                <Badge variant="outline">{generatedContent.passage_type}</Badge>
              </div>
            </CardHeader>
            <CardContent>
              {editMode ? (
                <Textarea
                  value={generatedContent.content}
                  onChange={(e) => handleEditContent("content", e.target.value)}
                  className="min-h-[300px] font-serif text-lg leading-relaxed"
                  data-testid="edit-content"
                />
              ) : (
                <ScrollArea className="h-[300px]">
                  <p className="passage-text whitespace-pre-wrap">{generatedContent.content}</p>
                </ScrollArea>
              )}

              {/* Summary */}
              {generatedContent.summary && (
                <div className="mt-6 p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-medium text-slate-700 mb-2">Summary</h4>
                  {editMode ? (
                    <Textarea
                      value={generatedContent.summary}
                      onChange={(e) => handleEditContent("summary", e.target.value)}
                      className="min-h-[80px]"
                    />
                  ) : (
                    <p className="text-slate-600">{generatedContent.summary}</p>
                  )}
                </div>
              )}

              {/* Vocabulary */}
              {generatedContent.vocabulary?.length > 0 && (
                <div className="mt-4 p-4 bg-sky-50 rounded-lg">
                  <h4 className="font-medium text-sky-700 mb-2">Key Vocabulary</h4>
                  <div className="flex flex-wrap gap-2">
                    {generatedContent.vocabulary.map((v, i) => (
                      <span key={i} className="px-3 py-1 bg-white rounded-lg text-sm border border-sky-200">
                        <strong>{v.word}</strong>: {v.meaning}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Questions */}
          <Card>
            <CardHeader>
              <CardTitle>Questions ({generatedContent.questions?.length || 0})</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {generatedContent.questions?.map((q, qIndex) => (
                <div
                  key={qIndex}
                  className="border rounded-xl overflow-hidden"
                  data-testid={`question-${qIndex}`}
                >
                  <button
                    onClick={() => setExpandedQuestion(expandedQuestion === qIndex ? null : qIndex)}
                    className="w-full flex items-center justify-between p-4 bg-slate-50 text-left"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center font-bold">
                        {qIndex + 1}
                      </span>
                      <div>
                        <p className="font-medium text-slate-900 line-clamp-1">{q.question_text}</p>
                        <Badge variant="outline" className="text-xs mt-1">
                          {questionTypes[q.question_type] || q.question_type}
                        </Badge>
                      </div>
                    </div>
                    {expandedQuestion === qIndex ? (
                      <ChevronUp className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    )}
                  </button>

                  {expandedQuestion === qIndex && (
                    <div className="p-4 space-y-4 animate-fade-in">
                      {/* Question Text */}
                      {editMode ? (
                        <div>
                          <Label>Question</Label>
                          <Textarea
                            value={q.question_text}
                            onChange={(e) => handleEditQuestion(qIndex, "question_text", e.target.value)}
                            className="mt-1"
                          />
                        </div>
                      ) : (
                        <p className="font-medium text-lg">{q.question_text}</p>
                      )}

                      {/* Options */}
                      <div className="space-y-2">
                        <Label>Options</Label>
                        {q.options.map((opt, oIndex) => (
                          <div
                            key={oIndex}
                            className={`flex items-center gap-3 p-3 rounded-lg ${
                              oIndex === q.correct_option
                                ? "bg-emerald-50 border-2 border-emerald-500"
                                : "bg-slate-50 border border-slate-200"
                            }`}
                          >
                            {editMode ? (
                              <>
                                <input
                                  type="radio"
                                  name={`correct-${qIndex}`}
                                  checked={oIndex === q.correct_option}
                                  onChange={() => handleEditQuestion(qIndex, "correct_option", oIndex)}
                                  className="w-4 h-4"
                                />
                                <Input
                                  value={opt}
                                  onChange={(e) => handleEditOption(qIndex, oIndex, e.target.value)}
                                  className="flex-1"
                                />
                              </>
                            ) : (
                              <>
                                <span className="font-bold text-slate-500">
                                  {String.fromCharCode(65 + oIndex)}.
                                </span>
                                <span className={oIndex === q.correct_option ? "font-medium text-emerald-700" : ""}>
                                  {opt}
                                </span>
                                {oIndex === q.correct_option && (
                                  <CheckCircle2 className="w-5 h-5 text-emerald-600 ml-auto" />
                                )}
                              </>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Explanation */}
                      <div className="bg-sky-50 p-4 rounded-lg">
                        <Label className="text-sky-700">Explanation</Label>
                        {editMode ? (
                          <Textarea
                            value={q.explanation}
                            onChange={(e) => handleEditQuestion(qIndex, "explanation", e.target.value)}
                            className="mt-1 bg-white"
                          />
                        ) : (
                          <p className="text-slate-700 mt-1">{q.explanation}</p>
                        )}
                      </div>

                      {/* Why Others Wrong */}
                      {q.why_others_wrong && (
                        <div className="bg-rose-50 p-4 rounded-lg">
                          <Label className="text-rose-700">Why Other Options Are Wrong</Label>
                          <ul className="mt-2 space-y-1">
                            {q.why_others_wrong.map((reason, rIndex) => (
                              <li key={rIndex} className="text-sm text-slate-600">
                                <strong>{String.fromCharCode(65 + rIndex)}:</strong>{" "}
                                {rIndex === q.correct_option ? (
                                  <span className="text-emerald-600">(Correct answer)</span>
                                ) : (
                                  reason
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Question Type Selector (Edit Mode) */}
                      {editMode && (
                        <div>
                          <Label>Question Type</Label>
                          <Select
                            value={q.question_type}
                            onValueChange={(v) => handleEditQuestion(qIndex, "question_type", v)}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(questionTypes).map(([key, label]) => (
                                <SelectItem key={key} value={key}>
                                  {label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex justify-between items-center p-4 bg-slate-100 rounded-xl">
            <Button
              variant="outline"
              onClick={() => setGeneratedContent(null)}
              data-testid="discard-btn"
            >
              Discard & Start Over
            </Button>
            <div className="flex gap-3">
              <Button
                onClick={handleSaveDraft}
                disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="save-draft-final-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save as Draft
              </Button>
            </div>
          </div>

          {/* Workflow Info */}
          <Card className="bg-slate-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-4 text-sm">
                <span className="text-slate-500">Workflow:</span>
                <Badge className="bg-amber-100 text-amber-700">DRAFT</Badge>
                <span className="text-slate-400">→</span>
                <Badge variant="outline">In Review</Badge>
                <span className="text-slate-400">→</span>
                <Badge variant="outline">Published</Badge>
              </div>
              <p className="text-xs text-slate-500 mt-2">
                After saving as draft, you can review and edit in the Passages section before publishing.
              </p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AIContentStudio;
